import React, { useEffect, useRef, useState, useMemo } from 'react';
import { collection, onSnapshot, query, where, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useGlobalCategories } from '../../hooks/useGlobalCategories';
import { useObjectCategories } from '../../hooks/useObjectCategories';
import { useLabelAttributes } from '../../hooks/useLabelAttributes';
import LabelListPanel from '../../components/LabelListPanel';
import AttributePanel from '../../components/AttributePanel';
import { LabelAttribute } from '../../types/labelAttribute';
import { 
  getRequiredSteps, 
  getLabelsForStep, 
  stepKeyFromMedia,
  stepKeyToRole,
  getStepDisplayLabel,
  type LabelData
} from '../../utils/submissionHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Multi-photo types
// ─────────────────────────────────────────────────────────────────────────────
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface SubmissionMediaItem {
  role: MediaRole;
  url: string;
  type?: string;
  storagePath?: string;
}

type Submission = {
  id: string;
  taskId: string;
  imageUrl?: string;
  mediaUrl?: string;
  createdAt?: any;
  // Single-photo labels (legacy)
  labels?: LabelData[];
  // Multi-photo support
  media?: SubmissionMediaItem[];
  labelsByRole?: Partial<Record<MediaRole, LabelData[]>>;
  // New stepKey-based labels (Phase D)
  labelsByStep?: Partial<Record<string, LabelData[]>>;
  imageAttributes?: Record<string, any>;
  taskTitle?: string;
  taskType?: string;
  category?: string;
  subcategory?: string;
  itemType?: string;
  contentLanguageCode?: string;
  contentLanguageLabel?: string;
  qaReviewReason?: string;
  qaReviewStatus?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Multi-photo helper functions
// ─────────────────────────────────────────────────────────────────────────────

// Check if submission is multi-photo (has media[] with 2+ items)
function isMultiPhotoSubmission(submission: Submission | null): boolean {
  return Array.isArray(submission?.media) && (submission?.media?.length ?? 0) >= 2;
}

// Get required roles for a submission (legacy function, now uses stepKeys internally)
// Phase A: Uses stepKeys internally but returns roles for UI compatibility
function getRequiredRoles(submission: Submission | null): MediaRole[] {
  if (!submission) return ['primary'];
  
  // Use stepKeys internally (derives from submission.media, NOT task.steps)
  const stepKeys = getRequiredSteps(submission);
  
  // Map stepKeys back to roles for UI display (legacy compatibility)
  const roles: MediaRole[] = [];
  for (const stepKey of stepKeys) {
    const role = stepKeyToRole(stepKey, submission);
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }
  
  // Fallback: if no roles found, use stepKey as role (for legacy compatibility)
  if (roles.length === 0) {
    return ['primary'];
  }
  
  return roles;
}

// Get secondary role for multi-photo submission
function getSecondaryRole(submission: Submission | null): MediaRole | null {
  const roles = getRequiredRoles(submission);
  return roles.length > 1 ? roles[1] : null;
}

// Get image URL for a specific role
function getImageUrlForRole(submission: Submission | null, role: MediaRole): string | null {
  if (!submission) return null;
  
  if (role === 'primary') {
    // Prefer imageUrl/mediaUrl for backward compatibility
    if (submission.imageUrl) return submission.imageUrl;
    if (submission.mediaUrl) return submission.mediaUrl;
    // Fallback to media array
    if (Array.isArray(submission.media)) {
      const primaryMedia = submission.media.find(m => m.role === 'primary');
      if (primaryMedia?.url) return primaryMedia.url;
    }
    return null;
  }
  
  // For secondary roles, look in media array
  if (Array.isArray(submission.media)) {
    const mediaItem = submission.media.find(m => m.role === role);
    return mediaItem?.url || null;
  }
  
  return null;
}

// Get labels for active role
function getActiveLabels(submission: Submission | null, activeRole: MediaRole): LabelData[] {
  if (!submission) return [];
  
  // Find corresponding stepKey for this role
  const mediaItem = submission.media?.find((m: any) => m.role === activeRole);
  const stepKey = stepKeyFromMedia(mediaItem);
  
  // Use helper to read labels with fallback priority
  return getLabelsForStep(submission, stepKey);
}

// Get display label for a role
function getRoleDisplayLabel(role: MediaRole, submission?: Submission | null): string {
  if (role === 'primary') {
    // Check if this is a before_after task
    if (submission?.media?.some(m => m.role === 'after')) {
      return 'Before';
    }
    return 'Primary';
  }
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

function resolveAttributeDefinitions(
  names: string[] | undefined,
  catalog: LabelAttribute[]
): LabelAttribute[] {
  if (!names?.length) return [];
  return names
    .map((name) => catalog.find((attr) => attr.name === name && attr.active !== false))
    .filter((attr): attr is LabelAttribute => Boolean(attr));
}

function getInstanceAttributeDefs(
  schema: any,
  label: any,
  catalog: LabelAttribute[],
  getCategoryById: (id: string) => any
): LabelAttribute[] {
  const globalNames: string[] = schema?.globalAttributes || [];
  const categoryId = label?.categoryId;
  const schemaClass = schema?.classes?.find(
    (c: any) => c.id === categoryId || c.name === categoryId
  );
  let classNames: string[] = [];
  if (schemaClass?.attributes?.length) {
    classNames = schemaClass.attributes;
  } else {
    const globalCategory = categoryId ? getCategoryById(categoryId) : null;
    if (
      globalCategory &&
      Array.isArray((globalCategory as any).attributes) &&
      (globalCategory as any).attributes.length
    ) {
      classNames = (globalCategory as any).attributes;
    }
  }
  const allNames = Array.from(new Set([...globalNames, ...classNames]));
  return resolveAttributeDefinitions(allNames, catalog);
}

function getImageAttributeDefs(schema: any, catalog: LabelAttribute[]): LabelAttribute[] {
  return resolveAttributeDefinitions(schema?.imageAttributes, catalog);
}

async function persistLabelAtIndex(
  submission: Submission,
  activeRole: MediaRole,
  labelIndex: number,
  updatedLabel: LabelData
): Promise<Submission> {
  if (isMultiPhotoSubmission(submission)) {
    const activeMediaItem = submission.media?.find((m: any) => m.role === activeRole);
    const stepKey = stepKeyFromMedia(activeMediaItem);

    const currentRoleLabels = [...(submission.labelsByRole?.[activeRole] || [])];
    currentRoleLabels[labelIndex] = updatedLabel;

    const updatedLabelsByRole = {
      ...submission.labelsByRole,
      [activeRole]: currentRoleLabels,
    };

    const currentStepLabels = [
      ...(submission.labelsByStep?.[stepKey] || submission.labelsByRole?.[activeRole] || []),
    ];
    currentStepLabels[labelIndex] = updatedLabel;

    const updatedLabelsByStep = {
      ...(submission.labelsByStep || {}),
      [stepKey]: currentStepLabels,
    };

    await updateDoc(doc(db, 'submissions', submission.id), {
      labelsByRole: updatedLabelsByRole,
      labelsByStep: updatedLabelsByStep,
    });

    return {
      ...submission,
      labelsByRole: updatedLabelsByRole,
      labelsByStep: updatedLabelsByStep,
    };
  }

  const updatedLabels = [...(submission.labels || [])];
  updatedLabels[labelIndex] = updatedLabel;

  const primaryMediaItem =
    submission.media?.find((m: any) => m.role === 'primary') ||
    (submission.media && submission.media.length > 0 ? submission.media[0] : null);
  const primaryStepKey = stepKeyFromMedia(primaryMediaItem);

  const updatedLabelsByStep = {
    ...(submission.labelsByStep || {}),
    [primaryStepKey]: updatedLabels,
  };

  await updateDoc(doc(db, 'submissions', submission.id), {
    labels: updatedLabels,
    labelsByStep: updatedLabelsByStep,
  });

  return {
    ...submission,
    labels: updatedLabels,
    labelsByStep: updatedLabelsByStep,
  };
}

type Task = {
  id: string;
  title: string;
  steps?: Array<{
    stepId: string;
    title: string;
    description: string;
    required: boolean;
    exampleImages: string[];
    dropdown?: any;
  }>;
  description?: string;
  category?: string;
  subcategory?: string;
  validationPrompt?: string;
  validationRules?: {
    requiredObjects?: string[];
    expectedLabels?: string[];
    expectedLabelCategories?: string[];
    captionKeywords?: string[];
    minConfidence?: number;
    customValidation?: string;
  };
  detection?: { enabled: boolean; schemaRef?: { id: string; version: string } };
};

export default function Review() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [activeQueueTab, setActiveQueueTab] = useState<'photo' | 'video'>('photo');
  const [completedSubmissions, setCompletedSubmissions] = useState<Submission[]>([]);
  const [selected, setSelected] = useState<Submission | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const [schema, setSchema] = useState<any>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selectedIdRef = useRef<string | null>(null);
  const navigate = useNavigate();
  
  // Flag modal state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flaggingLabel, setFlaggingLabel] = useState<{ index: number; label: any } | null>(null);
  const [flagType, setFlagType] = useState<'redo' | 'wrong_annotation' | 'annotation_not_needed' | 'other'>('redo');
  const [flagReason, setFlagReason] = useState('');

  // Attribute flag modal state
  type AttributeFlagType = 'wrong_value' | 'cannot_determine' | 'needs_review' | 'other';
  const [showAttributeFlagModal, setShowAttributeFlagModal] = useState(false);
  const [flaggingAttributeName, setFlaggingAttributeName] = useState<string | null>(null);
  const [attributeFlagType, setAttributeFlagType] = useState<AttributeFlagType>('wrong_value');
  const [attributeFlagReason, setAttributeFlagReason] = useState('');
  
  // Hide/show labels state
  const [hideAllLabels, setHideAllLabels] = useState(false);
  
  // Multi-photo role/step state
  const [activeRole, setActiveRole] = useState<MediaRole>('primary');
  const [activeStepKey, setActiveStepKey] = useState<string>('primary'); // For step-based tasks
  const [selectedLabelIndex, setSelectedLabelIndex] = useState<number | null>(null);
  
  // QA Review modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [qaReason, setQaReason] = useState("");

  useEffect(() => {
    document.title = 'Review — Pixano';
  }, []);
  
  // QA Review reasons for Review page
  const qaReviewReasons = [
    "Incorrect Task",
    "Task doesn't look correct",
    "Not Sure if Task is Correct",
    "Image Quality Questionable",
    "Questionable Approval",
    "inappropriate",
    "Possible Duplicate",
    "Looks like AI",
    "Looks like Computer Screen"
  ];
  
  // Global categories for proper label names
  const { getCategoryById } = useGlobalCategories();
  const { categories: objectCategories } = useObjectCategories();
  const { attributes: labelAttributes } = useLabelAttributes();

  // Create a map for object category names
  const objectCategoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    objectCategories.forEach(cat => {
      map.set(cat.id, cat.displayName || cat.name || cat.id);
    });
    return map;
  }, [objectCategories]);

  // Helper function to get category name from either schema, object categories, or global categories
  const getCategoryName = (categoryId: string) => {
    // First try to find in schema classes
    const schemaCategory = schema?.classes?.find((c: any) => c.id === categoryId);
    if (schemaCategory) {
      return schemaCategory.label || schemaCategory.name || categoryId;
    }
    
    // Then try to find in object categories (for expectedLabelCategories)
    const objectCategoryName = objectCategoryNameMap.get(categoryId);
    if (objectCategoryName) {
      return objectCategoryName;
    }
    
    // Then try to find in global categories
    const globalCategory = getCategoryById(categoryId);
    if (globalCategory) {
      return globalCategory.displayName || globalCategory.name || categoryId;
    }
    
    // Fallback to categoryId
    return categoryId;
  };

  const activeLabels = useMemo(
    () => (selected ? getActiveLabels(selected, activeRole) : []),
    [selected, activeRole]
  );

  const selectedLabel =
    selectedLabelIndex !== null && selectedLabelIndex < activeLabels.length
      ? activeLabels[selectedLabelIndex]
      : null;

  const imageLevelAttributes = useMemo(
    () => getImageAttributeDefs(schema, labelAttributes),
    [schema, labelAttributes]
  );

  const selectedLabelInstanceAttributes = useMemo(() => {
    if (!selectedLabel || !schema) return [];
    return getInstanceAttributeDefs(schema, selectedLabel, labelAttributes, getCategoryById);
  }, [selectedLabel, schema, labelAttributes, getCategoryById]);

  useEffect(() => {
    setSelectedLabelIndex(null);
  }, [selected?.id, activeRole, activeStepKey]);

  const handleLabelAttributeChange = async (next: Record<string, any>) => {
    if (!selected || selectedLabelIndex === null) return;

    const labels = getActiveLabels(selected, activeRole);
    const existingLabel = labels[selectedLabelIndex];
    if (!existingLabel) return;

    const nextAttributes = { ...next, state_label_source: 'reviewer_verified' };
    const updatedLabel = { ...existingLabel, attributes: nextAttributes };

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selected,
        activeRole,
        selectedLabelIndex,
        updatedLabel
      );
      setSelected(updatedSubmission);
      setSubmissions((prev) => prev.map((s) => (s.id === selected.id ? updatedSubmission : s)));
    } catch (error) {
      console.error('Error updating label attributes:', error);
      alert('Failed to update label attributes. Please try again.');
    }
  };

  const handleImageAttributeChange = async (next: Record<string, any>) => {
    if (!selected) return;

    try {
      await updateDoc(doc(db, 'submissions', selected.id), {
        imageAttributes: next,
      });
      const updatedSubmission = { ...selected, imageAttributes: next };
      setSelected(updatedSubmission);
      setSubmissions((prev) => prev.map((s) => (s.id === selected.id ? updatedSubmission : s)));
    } catch (error) {
      console.error('Error updating image attributes:', error);
      alert('Failed to update image attributes. Please try again.');
    }
  };

  const handleAttributeFlagClick = (attrName: string) => {
    setFlaggingAttributeName(attrName);
    const existing = selectedLabel?.attributeFlags?.[attrName];
    if (existing) {
      setAttributeFlagType((existing.flagType as AttributeFlagType) || 'wrong_value');
      setAttributeFlagReason(existing.flagType === 'other' ? existing.flagReason || '' : '');
    } else {
      setAttributeFlagType('wrong_value');
      setAttributeFlagReason('');
    }
    setShowAttributeFlagModal(true);
  };

  const closeAttributeFlagModal = () => {
    setShowAttributeFlagModal(false);
    setFlaggingAttributeName(null);
    setAttributeFlagType('wrong_value');
    setAttributeFlagReason('');
  };

  const handleAttributeFlagSubmit = async () => {
    if (!selected || selectedLabelIndex === null || !flaggingAttributeName) return;

    const finalReason =
      attributeFlagType === 'other' ? attributeFlagReason : attributeFlagType;
    if (attributeFlagType === 'other' && !attributeFlagReason.trim()) {
      alert('Please provide a reason for flagging.');
      return;
    }

    const labels = getActiveLabels(selected, activeRole);
    const existingLabel = labels[selectedLabelIndex];
    if (!existingLabel) return;

    const auth = getAuth();
    const uid = auth.currentUser?.uid || 'admin';

    const updatedLabel = {
      ...existingLabel,
      attributeFlags: {
        ...(existingLabel.attributeFlags || {}),
        [flaggingAttributeName]: {
          flagType: attributeFlagType,
          flagReason: finalReason,
          flaggedAt: Date.now(),
          flaggedBy: uid,
        },
      },
    };

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selected,
        activeRole,
        selectedLabelIndex,
        updatedLabel
      );
      setSelected(updatedSubmission);
      setSubmissions((prev) => prev.map((s) => (s.id === selected.id ? updatedSubmission : s)));
      closeAttributeFlagModal();
    } catch (error) {
      console.error('Error flagging attribute:', error);
      alert('Failed to flag attribute. Please try again.');
    }
  };

  const handleAttributeFlagRemove = async () => {
    if (!selected || selectedLabelIndex === null || !flaggingAttributeName) return;

    const labels = getActiveLabels(selected, activeRole);
    const existingLabel = labels[selectedLabelIndex];
    if (!existingLabel) return;

    const nextFlags = { ...(existingLabel.attributeFlags || {}) };
    delete nextFlags[flaggingAttributeName];

    const updatedLabel =
      Object.keys(nextFlags).length > 0
        ? { ...existingLabel, attributeFlags: nextFlags }
        : (() => {
            const { attributeFlags: _removed, ...rest } = existingLabel as any;
            return rest;
          })();

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selected,
        activeRole,
        selectedLabelIndex,
        updatedLabel
      );
      setSelected(updatedSubmission);
      setSubmissions((prev) => prev.map((s) => (s.id === selected.id ? updatedSubmission : s)));
      closeAttributeFlagModal();
    } catch (error) {
      console.error('Error removing attribute flag:', error);
      alert('Failed to remove attribute flag. Please try again.');
    }
  };

  const flaggingAttributeDisplayName =
    flaggingAttributeName
      ? selectedLabelInstanceAttributes.find((a) => a.name === flaggingAttributeName)?.displayName ||
        flaggingAttributeName
      : '';

  // Load queue of labeled submissions
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'submissions'), where('status', '==', 'labeled')),
      (snap) => {
        const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Submission[];
        setSubmissions(items);
        if (!selected && items.length) setSelected(items[0]);
      }
    );
    return () => unsub();
  }, [selected]);

  // Load completed submissions
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'submissions'), where('status', '==', 'completed')),
      (snap) => {
        const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Submission[];
        setCompletedSubmissions(items);
      }
    );
    return () => unsub();
  }, []);

  // Handle window resize to recalculate canvas size
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && image) {
        const canvas = canvasRef.current;
        // Get the actual image container (the one with bg-gray-100)
        const imageContainer = canvas.parentElement?.parentElement;
        if (imageContainer) {
          const maxWidth = imageContainer.clientWidth - 32;
          const maxHeight = imageContainer.clientHeight - 32;
          
          const scaleX = maxWidth / image.width;
          const scaleY = maxHeight / image.height;
          const scale = Math.min(scaleX, scaleY, 1);
          
          canvas.width = image.width * scale;
          canvas.height = image.height * scale;
          
          console.log('📐 [Review] Resize - Canvas size updated to:', {
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            scale
          });
          
          draw();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [image]);

  // Load task/schema when selection changes
  useEffect(() => {
    const load = async () => {
      if (!selected) {
        console.log('🚫 [Review] No selection, clearing state');
        setImage(null);
        setTask(null);
        setSchema(null);
        return;
      }
      
      // Reset to primary role when submission changes
      setActiveRole('primary');
      
      console.log('🔄 [Review] useEffect triggered for selected:', {
        id: selected.id,
        imageUrl: selected.imageUrl || selected.mediaUrl,
        isMultiPhoto: isMultiPhotoSubmission(selected),
        previousSelectedId: selectedIdRef.current
      });
      
      try {
        setLoading(true);
        setError(null);
        // Clear old image immediately when selection changes
        setImage(null);
        // Track the selected ID to prevent race conditions
        selectedIdRef.current = selected.id;
        console.log('📌 [Review] Updated selectedIdRef to:', selected.id);
        
        const tdoc = await getDoc(doc(db, 'tasks', selected.taskId));
        if (tdoc.exists()) setTask({ id: tdoc.id, ...(tdoc.data() as any) });
        const scId = (tdoc.data() as any)?.detection?.schemaRef?.id;
        console.log('🔍 [Review] Loading schema:', { scId, taskData: tdoc.data() });
        if (scId) {
          const sdoc = await getDoc(doc(db, 'labelSchemas', scId));
          if (sdoc.exists()) {
            const schemaData = { id: sdoc.id, ...sdoc.data() };
            console.log('📋 [Review] Schema loaded:', schemaData);
            setSchema(schemaData);
          } else {
            console.log('❌ [Review] Schema not found:', scId);
          }
        } else {
          console.log('❌ [Review] No schema ID found in task');
        }
      // load image with CORS retry - use role-based URL for multi-photo
      // Get image URL based on stepKey or role
      const baseUrl = (() => {
        if (activeStepKey && activeStepKey !== 'primary') {
          const mediaItem = selected?.media?.find((m: any) => stepKeyFromMedia(m) === activeStepKey);
          return mediaItem?.url;
        }
        return getImageUrlForRole(selected, activeRole);
      })();
      const loadingSubmissionId = selected.id; // Capture ID to check in async callbacks
      if (baseUrl) {
        // Add cache-busting parameter to force fresh load
        const url = baseUrl + (baseUrl.includes('?') ? '&' : '?') + `_t=${Date.now()}`;
        console.log('🖼️ [Review] Loading image for submission:', { 
          submissionId: selected.id, 
          url: baseUrl,
          cacheBustedUrl: url 
        });
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          console.log('✅ [Review] Image loaded for submission:', { 
            submissionId: loadingSubmissionId, 
            currentSelectedId: selectedIdRef.current,
            matches: selectedIdRef.current === loadingSubmissionId
          });
          // Only set image if this is still the selected submission
          if (selectedIdRef.current === loadingSubmissionId) {
            setImage(img);
            // Use setTimeout to ensure container has proper dimensions
            setTimeout(() => {
              if (canvasRef.current && selectedIdRef.current === loadingSubmissionId) {
                const canvas = canvasRef.current;
                // Get the actual image container (the one with bg-gray-100)
                const imageContainer = canvas.parentElement?.parentElement;
                if (imageContainer) {
                  const maxWidth = imageContainer.clientWidth - 32; // Account for padding (p-4 = 16px each side)
                  const maxHeight = imageContainer.clientHeight - 32;
                  
                  console.log('📐 [Review] Container dimensions:', {
                    containerWidth: imageContainer.clientWidth,
                    containerHeight: imageContainer.clientHeight,
                    maxWidth,
                    maxHeight,
                    imageWidth: img.width,
                    imageHeight: img.height
                  });
                  
                  // Calculate scale to fit image in container
                  const scaleX = maxWidth / img.width;
                  const scaleY = maxHeight / img.height;
                  const scale = Math.min(scaleX, scaleY, 1); // Don't scale up
                  
                  console.log('📐 [Review] Scale calculation:', { scaleX, scaleY, scale });
                  
                  // Set canvas size to scaled image size
                  canvas.width = img.width * scale;
                  canvas.height = img.height * scale;
                  
                  console.log('📐 [Review] Canvas size set to:', {
                    canvasWidth: canvas.width,
                    canvasHeight: canvas.height
                  });
                } else {
                  // Fallback to original size
                  canvas.width = img.width;
                  canvas.height = img.height;
                }
                draw();
              }
            }, 100); // Small delay to ensure layout is complete
          }
        };
        img.onerror = () => {
          console.log('❌ [Review] Image load error, retrying without CORS...');
          const imgRetry = new Image();
          imgRetry.onload = () => {
            console.log('✅ [Review] Image loaded (retry) for submission:', { 
              submissionId: loadingSubmissionId, 
              currentSelectedId: selectedIdRef.current,
              matches: selectedIdRef.current === loadingSubmissionId
            });
            // Only set image if this is still the selected submission
            if (selectedIdRef.current === loadingSubmissionId) {
              setImage(imgRetry);
              // Use setTimeout to ensure container has proper dimensions
              setTimeout(() => {
                if (canvasRef.current && selectedIdRef.current === loadingSubmissionId) {
                  const canvas = canvasRef.current;
                  // Get the actual image container (the one with bg-gray-100)
                  const imageContainer = canvas.parentElement?.parentElement;
                  if (imageContainer) {
                    const maxWidth = imageContainer.clientWidth - 32; // Account for padding (p-4 = 16px each side)
                    const maxHeight = imageContainer.clientHeight - 32;
                    
                    // Calculate scale to fit image in container
                    const scaleX = maxWidth / imgRetry.width;
                    const scaleY = maxHeight / imgRetry.height;
                    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up
                    
                    // Set canvas size to scaled image size
                    canvas.width = imgRetry.width * scale;
                    canvas.height = imgRetry.height * scale;
                  } else {
                    // Fallback to original size
                    canvas.width = imgRetry.width;
                    canvas.height = imgRetry.height;
                  }
                  draw();
                }
              }, 100); // Small delay to ensure layout is complete
            }
          };
          imgRetry.onerror = () => {
            console.error('❌ [Review] Failed to load image even without CORS');
            setError('Failed to load image');
          };
          imgRetry.src = url;
        };
        img.src = url;
      }
      } catch (err) {
        console.error('❌ [Review] Error loading submission:', err);
        setError('Failed to load submission data');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, activeRole]);

  const draw = () => {
    if (!canvasRef.current || !image) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Don't draw labels if hideAllLabels is true
    if (hideAllLabels) return;

    const scaleX = canvas.width / image.width;
    const scaleY = canvas.height / image.height;

    // Use role-based labels for multi-photo, legacy labels for single
    const labelsToRender = getActiveLabels(selected, activeRole);
    labelsToRender.forEach(l => {
      const [x1, y1, x2, y2] = l.bbox;
      const x = Math.round(x1 * scaleX);
      const y = Math.round(y1 * scaleY);
      const w = Math.round((x2 - x1) * scaleX);
      const h = Math.round((y2 - y1) * scaleY);

      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, w, h);

      // Get the actual category name from schema or global categories
      const name = getCategoryName(l.categoryId);
      
      // Debug logging
      const schemaCategory = schema?.classes?.find((c: any) => c.id === l.categoryId);
      const globalCategory = getCategoryById(l.categoryId);
      if (!schemaCategory && !globalCategory) {
        console.log('⚠️ [Review] Category not found:', { 
          categoryId: l.categoryId, 
          schemaClasses: schema?.classes,
          globalCategories: globalCategory
        });
      }
      
      // Draw label background
      ctx.fillStyle = 'rgba(16, 185, 129, 0.15)';
      ctx.fillRect(x, y, w, h);
      
      // Draw label text with background
      ctx.fillStyle = '#065f46';
      ctx.font = 'bold 12px sans-serif';
      const textMetrics = ctx.measureText(name);
      const textWidth = textMetrics.width;
      const textHeight = 16;
      
      // Draw text background
      ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
      ctx.fillRect(x + 2, y + 2, textWidth + 8, textHeight);
      
      // Draw text
      ctx.fillStyle = 'white';
      ctx.fillText(name, x + 6, y + 14);
    });
  };

  useEffect(() => { draw(); }, [image, selected?.labels, selected?.labelsByRole, schema, hideAllLabels, activeRole]);

  // Recalculate canvas size when image changes
  useEffect(() => {
    if (image && canvasRef.current) {
      const canvas = canvasRef.current;
      // Get the actual image container (the one with bg-gray-100)
      const imageContainer = canvas.parentElement?.parentElement;
      if (imageContainer) {
        const maxWidth = imageContainer.clientWidth - 32;
        const maxHeight = imageContainer.clientHeight - 32;
        
        const scaleX = maxWidth / image.width;
        const scaleY = maxHeight / image.height;
        const scale = Math.min(scaleX, scaleY, 1);
        
        canvas.width = image.width * scale;
        canvas.height = image.height * scale;
        
        console.log('📐 [Review] Image change - Canvas size updated to:', {
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          scale
        });
        
        draw();
      }
    }
  }, [image]);

  const approve = async () => {
    if (!selected) return;
    
    // Check if there are flagged labels - cannot approve if flags exist
    if (hasFlaggedLabels(selected)) {
      alert('Cannot approve submission with flagged labels. Please remove all flags first.');
      return;
    }
    
    const currentId = selected.id;
    try {
      await updateDoc(doc(db, 'submissions', currentId), {
        status: 'completed',
        approvedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
      });
      
      // Find next item before removing current one
      const currentIndex = submissions.findIndex(s => s.id === currentId);
      const nextItem = currentIndex >= 0 && currentIndex < submissions.length - 1 
        ? submissions[currentIndex + 1]
        : currentIndex > 0 
          ? submissions[currentIndex - 1]
          : null;
      
      console.log('✅ [Review] Approving item, moving to next:', {
        currentId,
        currentIndex,
        nextItemId: nextItem?.id,
        nextItemImageUrl: nextItem?.imageUrl || nextItem?.mediaUrl,
        submissionsLength: submissions.length
      });
      
      // Remove current item and select next
      setSubmissions(prev => prev.filter(s => s.id !== currentId));
      if (nextItem) {
        // Create a fresh copy to ensure React detects the change
        const freshNextItem = { ...nextItem };
        console.log('🔄 [Review] Setting selected to next item:', {
          id: freshNextItem.id,
          imageUrl: freshNextItem.imageUrl || freshNextItem.mediaUrl
        });
        setSelected(freshNextItem);
      } else {
        // No more items, clear everything
        setImage(null);
        setTask(null);
        setSchema(null);
        setSelected(null);
      }
    } catch (error) {
      console.error('❌ [Review] Error approving item:', error);
      alert('Failed to approve item. Please try again.');
    }
  };

  const handleQAClick = () => {
    setShowQAModal(true);
    setQaReason("");
  };

  const handleQAReasonChange = (reason: string) => {
    setQaReason(reason);
  };

  const handleQASubmit = async () => {
    if (!qaReason.trim()) {
      alert("Please select a QA Review reason.");
      return;
    }
    
    if (!selected) return;
    const currentId = selected.id;
    
    try {
      // Set qaFinalReviewStatus to 'pending' and keep status as 'approved'
      // This sends it to QA Final Review section while keeping it approved from user's perspective
      await updateDoc(doc(db, 'submissions', currentId), {
        qaFinalReviewStatus: 'pending',
        qaReviewReason: qaReason,
        status: 'approved', // Keep as approved so user is still paid
      });
      
      // Find next item before removing current one
      const currentIndex = submissions.findIndex(s => s.id === currentId);
      const nextItem = currentIndex >= 0 && currentIndex < submissions.length - 1 
        ? submissions[currentIndex + 1]
        : currentIndex > 0 
          ? submissions[currentIndex - 1]
          : null;
      
      console.log('🔍 [Review] Sending to QA Review, moving to next:', {
        currentId,
        currentIndex,
        nextItemId: nextItem?.id,
        nextItemImageUrl: nextItem?.imageUrl || nextItem?.mediaUrl,
        submissionsLength: submissions.length
      });
      
      // Close modal
      setShowQAModal(false);
      setQaReason("");
      
      // Remove current item and select next
      setSubmissions(prev => prev.filter(s => s.id !== currentId));
      if (nextItem) {
        // Create a fresh copy to ensure React detects the change
        const freshNextItem = { ...nextItem };
        console.log('🔄 [Review] Setting selected to next item:', {
          id: freshNextItem.id,
          imageUrl: freshNextItem.imageUrl || freshNextItem.mediaUrl
        });
        setSelected(freshNextItem);
      } else {
        // No more items, clear everything
        setImage(null);
        setTask(null);
        setSchema(null);
        setSelected(null);
      }
    } catch (error) {
      console.error('Error sending to QA Review:', error);
      alert('Error sending submission to QA Review. Please try again.');
    }
  };

  const handleQAModalClose = () => {
    setShowQAModal(false);
    setQaReason("");
  };

  // Check if submission has any flagged labels (works for both single and multi-photo)
  const hasFlaggedLabels = (submission: Submission | null): boolean => {
    if (!submission) return false;
    
    // Check multi-photo labelsByRole
    if (isMultiPhotoSubmission(submission) && submission.labelsByRole) {
      for (const role of Object.keys(submission.labelsByRole) as MediaRole[]) {
        const roleLabels = submission.labelsByRole[role] || [];
        if (roleLabels.some(label => label.flagged === true)) {
          return true;
        }
      }
      return false;
    }
    
    // Single-photo: check legacy labels
    if (!submission.labels) return false;
    return submission.labels.some((label: any) => label.flagged === true);
  };

  // Handle flag button click
  const handleFlagClick = (labelIndex: number, label: any) => {
    setFlaggingLabel({ index: labelIndex, label });
    setFlagType('redo');
    setFlagReason('');
    setShowFlagModal(true);
  };

  // Handle flag submission
  const handleFlagSubmit = async () => {
    if (!selected || !flaggingLabel) return;
    
    const finalReason = flagType === 'other' ? flagReason : flagType;
    if (flagType === 'other' && !flagReason.trim()) {
      alert('Please provide a reason for flagging.');
      return;
    }

    try {
      if (isMultiPhotoSubmission(selected)) {
        // Multi-photo: dual-write to labelsByRole (legacy) and labelsByStep (new)
        // CRITICAL: Derive stepKey from media item lookup (same pattern as UI tabs)
        const activeMediaItem = selected.media?.find((m: any) => m.role === activeRole);
        const activeStepKey = stepKeyFromMedia(activeMediaItem);
        
        const currentRoleLabels = [...(selected.labelsByRole?.[activeRole] || [])];
        currentRoleLabels[flaggingLabel.index] = {
          ...currentRoleLabels[flaggingLabel.index],
          flagged: true,
          flagType,
          flagReason: finalReason
        };

        const updatedLabelsByRole = {
          ...selected.labelsByRole,
          [activeRole]: currentRoleLabels
        };
        
        // Also update labelsByStep with same change
        const currentStepLabels = [...(selected.labelsByStep?.[activeStepKey] || selected.labelsByRole?.[activeRole] || [])];
        currentStepLabels[flaggingLabel.index] = {
          ...currentStepLabels[flaggingLabel.index],
          flagged: true,
          flagType,
          flagReason: finalReason
        };
        
        const updatedLabelsByStep = {
          ...(selected.labelsByStep || {}),
          [activeStepKey]: currentStepLabels
        };

        await updateDoc(doc(db, 'submissions', selected.id), {
          labelsByRole: updatedLabelsByRole,
          labelsByStep: updatedLabelsByStep
        });

        const updatedSubmission = {
          ...selected,
          labelsByRole: updatedLabelsByRole,
          labelsByStep: updatedLabelsByStep
        };
        setSelected(updatedSubmission);
        setSubmissions(prev => prev.map(s => s.id === selected.id ? updatedSubmission : s));
      } else {
        // Single-photo: update legacy labels field, also add labelsByStep['primary']
        const updatedLabels = [...(selected.labels || [])];
        updatedLabels[flaggingLabel.index] = {
          ...updatedLabels[flaggingLabel.index],
          flagged: true,
          flagType,
          flagReason: finalReason
        };
        
        // Derive stepKey for single-photo
        const primaryMediaItem = selected.media?.find((m: any) => m.role === 'primary') || 
                                 (selected.media && selected.media.length > 0 ? selected.media[0] : null);
        const primaryStepKey = stepKeyFromMedia(primaryMediaItem);
        
        const updatedLabelsByStep = {
          ...(selected.labelsByStep || {}),
          [primaryStepKey]: updatedLabels
        };

        await updateDoc(doc(db, 'submissions', selected.id), {
          labels: updatedLabels,  // Legacy format
          labelsByStep: updatedLabelsByStep  // New format (additive)
        });

        const updatedSubmission = {
          ...selected,
          labels: updatedLabels,
          labelsByStep: updatedLabelsByStep
        };
        setSelected(updatedSubmission);
        setSubmissions(prev => prev.map(s => s.id === selected.id ? updatedSubmission : s));
      }

      // Close modal
      setShowFlagModal(false);
      setFlaggingLabel(null);
      setFlagType('redo');
      setFlagReason('');
    } catch (error) {
      console.error('Error flagging label:', error);
      alert('Failed to flag label. Please try again.');
    }
  };

  const sendBack = async () => {
    if (!selected) return;
    
    // Show confirmation popup
    const confirmed = window.confirm('Are you sure you want to send this submission back for re-annotation? All labels will be preserved.');
    if (!confirmed) {
      return;
    }

    const currentId = selected.id;
    
    try {
      // Send back to workbench but keep all labels (including flagged ones)
      if (isMultiPhotoSubmission(selected)) {
        // Multi-photo: Do NOT write labels field, preserve labelsByRole
        await updateDoc(doc(db, 'submissions', currentId), {
          status: 'needs_relabeling',
          relabelRequestedAt: serverTimestamp()
          // labelsByRole is preserved automatically since we're using updateDoc
        });
      } else {
        // Single-photo: keep existing behavior
    await updateDoc(doc(db, 'submissions', currentId), {
      status: 'needs_relabeling',
      relabelRequestedAt: serverTimestamp(),
        // Keep all labels including flagged ones
        labels: selected.labels
    });
      }
    
    // Find next item before removing current one
    const currentIndex = submissions.findIndex(s => s.id === currentId);
    const nextItem = currentIndex >= 0 && currentIndex < submissions.length - 1 
      ? submissions[currentIndex + 1]
      : currentIndex > 0 
        ? submissions[currentIndex - 1]
        : null;
    
      console.log('↩️ [Review] Sending back item with flags, moving to next:', {
      currentId,
      currentIndex,
      nextItemId: nextItem?.id,
        flaggedLabels: selected.labels?.filter((l: any) => l.flagged).length || 0,
      submissionsLength: submissions.length
    });
    
    // Remove current item and select next
    setSubmissions(prev => prev.filter(s => s.id !== currentId));
    if (nextItem) {
      // Create a fresh copy to ensure React detects the change
      const freshNextItem = { ...nextItem };
      console.log('🔄 [Review] Setting selected to next item:', {
        id: freshNextItem.id,
        imageUrl: freshNextItem.imageUrl || freshNextItem.mediaUrl
      });
      setSelected(freshNextItem);
    } else {
      // No more items, clear everything
      setImage(null);
      setTask(null);
      setSchema(null);
      setSelected(null);
      }
    } catch (error) {
      console.error('Error sending back:', error);
      alert('Failed to send back for redo. Please try again.');
    }
  };

  if (!selected) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-4">Review</h1>
        <p className="text-gray-600">No labeled submissions awaiting review.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading submission...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Review</h1>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideAllLabels}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setHideAllLabels(newValue);
                }}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Hide Objects</span>
            </label>
            <div className="space-x-2">
              <button
                onClick={handleQAClick}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                QA Review
              </button>
            <button
              onClick={sendBack}
              className="px-4 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600"
            >
              Redo Annotation
            </button>
            <button
              onClick={approve}
              disabled={hasFlaggedLabels(selected)}
              className={`px-4 py-2 rounded-lg ${
                hasFlaggedLabels(selected)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title={hasFlaggedLabels(selected) ? 'Remove all flags before approving' : ''}
            >
              Approve
            </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: image */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Task Name, Category, Subcategory above image */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Task: {task?.title || selected?.taskTitle || '-'}
              </h2>
              <div className="flex items-center gap-6 text-base">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-700">Category:</span>
                  <span className="text-gray-900">{task?.category || selected?.category || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-700">Subcategory:</span>
                  <span className="text-gray-900">{task?.subcategory || selected?.subcategory || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-700">Item Type:</span>
                  <span className="text-gray-900">{selected?.itemType || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-700">Language:</span>
                  <span className="text-gray-900">{selected?.contentLanguageLabel || selected?.contentLanguageCode || '-'}</span>
                </div>
              </div>
            </div>
          </div>
          
          {image ? (
            <div className="flex-1 flex flex-col bg-gray-100 p-4 min-h-0">
              {/* Multi-photo Step/Role Toggle */}
              {isMultiPhotoSubmission(selected) && (() => {
                // Check if we should use stepKey-based tabs
                const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                const stepKeys = getRequiredSteps(selected);
                const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                
                return (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Viewing:</span>
                        <div className="flex gap-1">
                          {shouldUseStepKeyTabs ? (
                            // StepKey-based tabs
                            stepKeys.map((stepKey) => {
                              const isActive = activeStepKey === stepKey;
                              const stepLabels = getLabelsForStep(selected, stepKey);
                              const hasFlagged = stepLabels.some(l => l.flagged);
                              const displayLabel = getStepDisplayLabel(stepKey, task);
                              
                              return (
                                <button
                                  key={stepKey}
                                  onClick={() => {
                                    setActiveStepKey(stepKey);
                                    const mediaItem = selected?.media?.find((m: any) => stepKeyFromMedia(m) === stepKey);
                                    setActiveRole(mediaItem?.role || 'primary');
                                  }}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {displayLabel}
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    isActive ? 'bg-blue-500' : 'bg-gray-200'
                                  }`}>
                                    {stepLabels.length}
                                  </span>
                                  {hasFlagged && (
                                    <span className="text-yellow-400" title="Has flagged labels">⚠</span>
                                  )}
                                </button>
                              );
                            })
                          ) : (
                            // Role-based tabs (legacy)
                            getRequiredRoles(selected).map((role) => {
                              const isActive = activeRole === role;
                              // Use helper to read labels with fallback priority
                              const mediaItem = selected?.media?.find((m: any) => m.role === role);
                              const stepKey = stepKeyFromMedia(mediaItem);
                              const roleLabels = getLabelsForStep(selected, stepKey);
                              const hasFlagged = roleLabels.some(l => l.flagged);
                              
                              return (
                                <button
                                  key={role}
                                  onClick={() => {
                                    setActiveRole(role);
                                    setActiveStepKey(stepKey);
                                  }}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {getRoleDisplayLabel(role, selected)}
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    isActive ? 'bg-blue-500' : 'bg-gray-200'
                                  }`}>
                                    {roleLabels.length}
                                  </span>
                                  {hasFlagged && (
                                    <span className="text-yellow-400" title="Has flagged labels">⚠</span>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-purple-600 font-medium">
                        📷 Multi-Photo Submission
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Canvas Container */}
              <div className="flex-1 flex items-center justify-center min-h-0">
              <div className="relative max-w-full max-h-full">
                <canvas 
                  ref={canvasRef} 
                  className="border border-gray-300 cursor-crosshair max-w-full max-h-full"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="text-gray-500 mb-2">Loading image...</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="text-gray-500 mb-2">{error}</div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="text-gray-500 mb-2">No image loaded</div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: labels and queue */}
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Validation Prompt Panel */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold mb-2 text-lg">Validation Prompt</h3>
              <div className="text-sm text-gray-700">
                {task?.validationPrompt || '-'}
              </div>
            </div>

            {/* Required Objects Panel */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold mb-2 text-sm">Expected Labels</h3>
              {(() => {
                const expectedLabels = task?.validationRules?.expectedLabels || [];
                const expectedCategories = task?.validationRules?.expectedLabelCategories || [];
                // Use active role's labels for multi-photo, legacy labels for single
                const submissionLabels = getActiveLabels(selected, activeRole);
                
                // Get all label categoryIds from the submission (normalized to lowercase for case-insensitive matching)
                const submissionCategoryIds = new Set(submissionLabels.map((l: any) => l.categoryId?.toLowerCase()));
                
                // Helper to get label info (for flexible matching)
                const getLabelInfo = (labelCategoryId: string) => {
                  // First check schema classes
                  const schemaClass = schema?.classes?.find((c: any) => c.id === labelCategoryId);
                  if (schemaClass) {
                    return {
                      id: schemaClass.id,
                      name: schemaClass.name,
                      displayName: schemaClass.displayName || schemaClass.label,
                      category: schemaClass.category
                    };
                  }
                  
                  // Then check global categories
                  const globalCategory = getCategoryById(labelCategoryId);
                  if (globalCategory) {
                    return {
                      id: globalCategory.id,
                      name: globalCategory.name,
                      displayName: globalCategory.displayName,
                      category: globalCategory.category
                    };
                  }
                  
                  return {
                    id: labelCategoryId,
                    name: labelCategoryId,
                    displayName: labelCategoryId,
                    category: null
                  };
                };
                
                // Build a set of all label identifiers (ID, name, displayName) for flexible matching
                const labelIdentifiers = new Set<string>();
                submissionLabels.forEach((label: any) => {
                  const labelInfo = getLabelInfo(label.categoryId);
                  // Add ID
                  if (labelInfo.id) labelIdentifiers.add(labelInfo.id.toLowerCase());
                  // Add name
                  if (labelInfo.name) labelIdentifiers.add(labelInfo.name.toLowerCase());
                  // Add displayName
                  if (labelInfo.displayName) labelIdentifiers.add(labelInfo.displayName.toLowerCase());
                });
                
                // Helper to get a label's category (the category it belongs to, not its ID)
                const getLabelCategory = (labelCategoryId: string): string | null => {
                  const labelInfo = getLabelInfo(labelCategoryId);
                  return labelInfo.category || null;
                };
                
                // Get all categories that labels belong to
                const labelCategories = new Set<string>();
                submissionLabels.forEach((label: any) => {
                  const labelCategory = getLabelCategory(label.categoryId);
                  if (labelCategory) {
                    labelCategories.add(labelCategory);
                  }
                });
                
                // Check if an expected label is found in submission
                // An expected label is found if any label matches by ID, name, or displayName
                const isExpectedLabelFound = (expectedLabel: string) => {
                  const normalized = expectedLabel.toLowerCase().trim();
                  
                  // Direct ID match (case-insensitive)
                  if (submissionCategoryIds.has(normalized)) {
                    return true;
                  }
                  
                  // Check if any label's ID, name, or displayName matches
                  if (labelIdentifiers.has(normalized)) {
                    return true;
                  }
                  
                  // Also check if the expected label matches any label's ID, name, or displayName
                  // (in case expected label is stored as a name/displayName but we need to match by ID)
                  for (const label of submissionLabels) {
                    const labelInfo = getLabelInfo(label.categoryId);
                    if (
                      labelInfo.id?.toLowerCase() === normalized ||
                      labelInfo.name?.toLowerCase() === normalized ||
                      labelInfo.displayName?.toLowerCase() === normalized
                    ) {
                      return true;
                    }
                  }
                  
                  return false;
                };
                
                // Check if an expected category is found in submission
                // An expected category is found if at least one label belongs to that category
                const isExpectedCategoryFound = (categoryId: string) => {
                  return labelCategories.has(categoryId);
                };
                
                const hasAnyRequirements = expectedLabels.length > 0 || expectedCategories.length > 0;
                
                if (!hasAnyRequirements) {
                  return <p className="text-xs text-gray-500">No required objects specified</p>;
                }
                
                return (
                  <div className="space-y-3">
                    {/* Expected Labels Section */}
                    {expectedLabels.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-600 mb-1">Expected Labels:</div>
                <div className="grid grid-cols-2 gap-1">
                          {expectedLabels.map((labelId: string, index: number) => {
                            const isFound = isExpectedLabelFound(labelId);
                            const displayName = getCategoryName(labelId);
                            return (
                              <div
                                key={index}
                                className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${
                                  isFound
                                    ? 'bg-green-50 border-green-300 text-green-900'
                                    : 'bg-gray-50 border-gray-200 text-gray-900'
                                }`}
                              >
                                {isFound ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">○</span>
                                )}
                                <span className="truncate" title={displayName}>{displayName}</span>
                    </div>
                            );
                          })}
                </div>
                      </div>
                    )}
                    
                    {/* Expected Categories Section */}
                    {expectedCategories.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-600 mb-1">Expected Categories:</div>
                        <div className="grid grid-cols-2 gap-1">
                          {expectedCategories.map((categoryId: string, index: number) => {
                            const isFound = isExpectedCategoryFound(categoryId);
                            const displayName = getCategoryName(categoryId);
                            return (
                              <div
                                key={index}
                                className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${
                                  isFound
                                    ? 'bg-green-50 border-green-300 text-green-900'
                                    : 'bg-gray-50 border-gray-200 text-gray-900'
                                }`}
                              >
                                {isFound ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">○</span>
                                )}
                                <span className="truncate" title={displayName}>{displayName}</span>
                              </div>
                            );
                          })}
          </div>
        </div>
      )}

      {/* QA Review Modal */}
      {showQAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Send to QA Review</h2>
              <button onClick={handleQAModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {selected && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Submission ID:</strong> {selected.id.slice(-8)}
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select QA Review Reason:
              </label>
              <select 
                value={qaReason} 
                onChange={(e) => handleQAReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose a reason...</option>
                {qaReviewReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleQAModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQASubmit}
                disabled={!qaReason.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Send to QA Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
})()}
            </div>

            {/* Labels Panel */}
            <LabelListPanel
              labels={activeLabels}
              submission={selected}
              activeRole={activeRole}
              onFlagClick={handleFlagClick}
              getCategoryName={getCategoryName}
              getCategoryDescription={(categoryId: string) => {
                const schemaCategory = schema?.classes?.find((c: any) => c.id === categoryId);
                const globalCategory = getCategoryById(categoryId);
                return schemaCategory?.description || globalCategory?.description || '';
              }}
              variant="review"
              showRoleBreakdown={true}
              hideWhenEmpty={false}
              selectedIndex={selectedLabelIndex}
              onSelect={setSelectedLabelIndex}
            />

            {imageLevelAttributes.length > 0 && (
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold mb-3">Image</h3>
                <AttributePanel
                  attributes={imageLevelAttributes}
                  values={selected?.imageAttributes || {}}
                  onChange={handleImageAttributeChange}
                  compact
                />
              </div>
            )}

            {selectedLabel && selectedLabelInstanceAttributes.length > 0 && (
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold mb-3">Object Attributes</h3>
                <AttributePanel
                  attributes={selectedLabelInstanceAttributes}
                  values={selectedLabel.attributes || {}}
                  onChange={handleLabelAttributeChange}
                  flags={selectedLabel.attributeFlags || {}}
                  onFlagAttribute={handleAttributeFlagClick}
                  compact
                />
              </div>
            )}

            {/* Queue Panel */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex gap-2 border-b mb-2">
                <button
                  onClick={() => setActiveQueueTab('photo')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeQueueTab === 'photo'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Photo
                </button>
                <button
                  onClick={() => setActiveQueueTab('video')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeQueueTab === 'video'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Video
                </button>
              </div>
              <h3 className="font-semibold">Awaiting Review ({submissions.filter(s => activeQueueTab === 'photo' ? s.taskType !== 'Video' : s.taskType === 'Video').length})</h3>
            </div>
            <div className="flex-1 overflow-auto p-2 space-y-2">
              {submissions.filter(s => activeQueueTab === 'photo' ? s.taskType !== 'Video' : s.taskType === 'Video').map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelected(s)}
                  className={`w-full text-left p-2 rounded border ${selected?.id === s.id ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                    <img 
                      className="w-12 h-12 object-cover rounded" 
                      src={s.imageUrl || s.mediaUrl}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                      {/* Multi-photo badge */}
                      {isMultiPhotoSubmission(s) && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {getRequiredRoles(s).length}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        Task Name: {s.taskTitle || task?.title || '-'}
                        {isMultiPhotoSubmission(s) && <span className="text-[10px] text-purple-600">📷</span>}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        Category: {s.category || task?.category || '-'}
                        {s.contentLanguageLabel && (
                          <span className="text-xs text-gray-500 ml-1">
                            · {s.contentLanguageLabel}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-blue-600">
                        {isMultiPhotoSubmission(s) ? (
                          <>Labels: {getRequiredRoles(s).map(role => 
                            (() => {
                              const mediaItem = s.media?.find((m: any) => m.role === role);
                              const stepKey = stepKeyFromMedia(mediaItem);
                              const labels = getLabelsForStep(s, stepKey);
                              return `${getRoleDisplayLabel(role, s).slice(0,3)}: ${labels.length}`;
                            })()
                          ).join(', ')}</>
                        ) : (
                          <>Qty labels: {s.labels?.length || 0}</>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Flag Modal */}
            {showFlagModal && flaggingLabel && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h2 className="text-xl font-semibold mb-4">Flag Label</h2>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Flagging:
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="flagType"
                          value="redo"
                          checked={flagType === 'redo'}
                          onChange={(e) => {
                            setFlagType(e.target.value as any);
                            setFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Redo</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="flagType"
                          value="wrong_annotation"
                          checked={flagType === 'wrong_annotation'}
                          onChange={(e) => {
                            setFlagType(e.target.value as any);
                            setFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Wrong Annotation</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="flagType"
                          value="annotation_not_needed"
                          checked={flagType === 'annotation_not_needed'}
                          onChange={(e) => {
                            setFlagType(e.target.value as any);
                            setFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Annotation Not Needed</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="flagType"
                          value="other"
                          checked={flagType === 'other'}
                          onChange={(e) => setFlagType(e.target.value as any)}
                          className="mr-2"
                        />
                        <span>Other</span>
                      </label>
                    </div>
                    {flagType === 'other' && (
                      <textarea
                        value={flagReason}
                        onChange={(e) => setFlagReason(e.target.value)}
                        placeholder="Please provide a reason..."
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    )}
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        setShowFlagModal(false);
                        setFlaggingLabel(null);
                        setFlagType('redo');
                        setFlagReason('');
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFlagSubmit}
                      disabled={flagType === 'other' && !flagReason.trim()}
                      className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Flag Label
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Attribute Flag Modal */}
            {showAttributeFlagModal && flaggingAttributeName && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h2 className="text-xl font-semibold mb-4">
                    Flag Attribute: {flaggingAttributeDisplayName}
                  </h2>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Flagging:
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="attributeFlagType"
                          value="wrong_value"
                          checked={attributeFlagType === 'wrong_value'}
                          onChange={() => {
                            setAttributeFlagType('wrong_value');
                            setAttributeFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Wrong Value</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="attributeFlagType"
                          value="cannot_determine"
                          checked={attributeFlagType === 'cannot_determine'}
                          onChange={() => {
                            setAttributeFlagType('cannot_determine');
                            setAttributeFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Cannot Determine</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="attributeFlagType"
                          value="needs_review"
                          checked={attributeFlagType === 'needs_review'}
                          onChange={() => {
                            setAttributeFlagType('needs_review');
                            setAttributeFlagReason('');
                          }}
                          className="mr-2"
                        />
                        <span>Needs Review</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="attributeFlagType"
                          value="other"
                          checked={attributeFlagType === 'other'}
                          onChange={() => setAttributeFlagType('other')}
                          className="mr-2"
                        />
                        <span>Other</span>
                      </label>
                    </div>
                    {attributeFlagType === 'other' && (
                      <textarea
                        value={attributeFlagReason}
                        onChange={(e) => setAttributeFlagReason(e.target.value)}
                        placeholder="Please provide a reason..."
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    )}
                  </div>
                  <div className="flex justify-between">
                    {selectedLabel?.attributeFlags?.[flaggingAttributeName] && (
                      <button
                        onClick={handleAttributeFlagRemove}
                        className="px-4 py-2 text-red-600 border border-red-300 rounded hover:bg-red-50"
                      >
                        Remove Flag
                      </button>
                    )}
                    <div className="flex justify-end space-x-2 flex-1">
                      <button
                        onClick={closeAttributeFlagModal}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAttributeFlagSubmit}
                        disabled={attributeFlagType === 'other' && !attributeFlagReason.trim()}
                        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Flag Attribute
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Completed Items Panel */}
            <div className="p-4 border-t bg-green-50">
              <h3 className="font-semibold text-green-800">✅ Completed & Ready for Export ({completedSubmissions.length})</h3>
              <div className="mt-2 text-xs text-green-600">
                These items have been approved and are ready for data export.
              </div>
              {completedSubmissions.length > 0 && (
                <div className="mt-2 max-h-32 overflow-auto space-y-1">
                  {completedSubmissions.slice(0, 5).map(s => (
                    <div key={s.id} className="flex items-center space-x-2 p-1 bg-white rounded border">
                      <div className="relative">
                      <img 
                        className="w-8 h-8 object-cover rounded" 
                        src={s.imageUrl || s.mediaUrl}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                        {isMultiPhotoSubmission(s) && (
                          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-purple-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                            {getRequiredRoles(s).length}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{s.id.slice(-8)}</div>
                        <div className="text-xs text-gray-500">
                          {isMultiPhotoSubmission(s) 
                            ? `${Object.values(s.labelsByRole || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0)} labels (${getRequiredRoles(s).length} img)`
                            : `${s.labels?.length || 0} labels`
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  {completedSubmissions.length > 5 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{completedSubmissions.length - 5} more...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


